import { deepEqual } from '@/utils/function'
import React, { createContext, useContext, useState } from 'react'
import { FieldValues, UseFormReturn } from 'react-hook-form'

type FormPersistenceContextType = {
    shouldRestore: boolean
    acceptRestore: () => void
    declineRestore: () => void
    registerForm: <T extends FieldValues>(
        form: UseFormReturn<T>,
        key: string,
        defaultValues: T
    ) => void
}

const FormPersistenceContext = createContext<FormPersistenceContextType | null>(
    null
)

export const FormPersistenceProvider = ({
    children,
}: {
    children: React.ReactNode
}) => {
    const [shouldRestore, setShouldRestore] = useState(false)
    const [draft, setDraft] = useState<any>(null)
    const [formMeta, setFormMeta] = useState<{
        form: UseFormReturn<any>
        key: string
        defaultValues: any
    } | null>(null)

    const registerForm = (
        form: UseFormReturn<any>,
        key: string,
        defaultValues: any
    ) => {
        const saved = localStorage.getItem(key)
        if (!saved) {
            form.reset(defaultValues)
            return
        }

        try {
            const parsed = JSON.parse(saved)
            const isSame = deepEqual(parsed, defaultValues)
            if (!isSame) {
                setDraft(parsed)
                setFormMeta({ form, key, defaultValues })
                setShouldRestore(true)
            } else {
                form.reset(parsed)
            }
        } catch {
            form.reset(defaultValues)
        }
    }

    const acceptRestore = () => {
        if (formMeta && draft) {
            // Step 1: Reset form with draft data
            formMeta.form.reset(draft, {
                keepErrors: false,
                keepDirty: false,
                keepIsSubmitted: false,
                keepTouched: false,
                keepIsValid: false,
                keepSubmitCount: false,
                keepDefaultValues: false,
            })

            // Step 2: Force update watched fields multiple times to trigger useWatch
            setTimeout(() => {
                const values = formMeta.form.getValues()
                const domainFields =
                    values?.order?.products?.[0]?.service_domain_metadata
                        ?.domain_extra_fields

                if (domainFields) {
                    // Force trigger useWatch by setting values multiple times
                    const updateField = (fieldName: string, value: any) => {
                        if (value) {
                            const path =
                                `order.products.0.service_domain_metadata.domain_extra_fields.${fieldName}` as any
                            // Set to empty first to force change detection
                            formMeta.form.setValue(path, null, {
                                shouldValidate: false,
                            })
                            // Then set to actual value
                            setTimeout(() => {
                                formMeta.form.setValue(path, value, {
                                    shouldValidate: false,
                                    shouldTouch: true,
                                    shouldDirty: true,
                                })
                            }, 50)
                        }
                    }

                    // Update country fields first (dependencies)
                    updateField('ownerCountry', domainFields.ownerCountry)
                    updateField('uiCountry', domainFields.uiCountry)
                    updateField('adminCountry', domainFields.adminCountry)

                    // Then update province/district (after country is set)
                    setTimeout(() => {
                        updateField('ownerProvince', domainFields.ownerProvince)
                        updateField('ownerDistrict', domainFields.ownerDistrict)
                        updateField('uiProvince', domainFields.uiProvince)
                        updateField('uiDistrict', domainFields.uiDistrict)
                        updateField('adminProvince', domainFields.adminProvince)
                        updateField('adminDistrict', domainFields.adminDistrict)
                    }, 100)
                }

                // Step 3: Clear errors after all updates
                setTimeout(() => {
                    formMeta.form.clearErrors()
                }, 500)
            }, 150)
        }
        setShouldRestore(false)
        setDraft(null)
    }

    const declineRestore = () => {
        if (formMeta) {
            localStorage.removeItem(formMeta.key)
            formMeta.form.reset(formMeta.defaultValues)
        }
        setShouldRestore(false)
        setDraft(null)
    }

    return (
        <FormPersistenceContext.Provider
            value={{
                shouldRestore,
                acceptRestore,
                declineRestore,
                registerForm,
            }}
        >
            {children}
        </FormPersistenceContext.Provider>
    )
}

export const useFormPersistenceContext = () => {
    const ctx = useContext(FormPersistenceContext)
    if (!ctx)
        throw new Error(
            'useFormPersistenceContext must be used inside <FormPersistenceProvider>'
        )
    return ctx
}
